import { useCallback, useEffect, useMemo, useState } from "react";
import { useGame } from "@/context/GameContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ITEM_ICON, itemName, SKILL_LABEL, skillIcon } from "@/lib/gameData";
import {
  ScrollText, Check, Circle, Lock, Coins, Package, Sparkles,
  ChevronDown, ChevronRight, Trophy, MapPin, User, Flame,
} from "lucide-react";

function classForStatus(status) {
  switch (status) {
    case "completed": return "border-emerald-800/60";
    case "active":    return "border-amber-700";
    case "available": return "border-stone-600";
    default:          return "border-stone-800 opacity-60";
  }
}

function StatusBadge({ status }) {
  const cfg = {
    completed: { text: "Completed", cls: "bg-emerald-900/40 border-emerald-700 text-emerald-300" },
    active:    { text: "In progress", cls: "bg-amber-900/40 border-amber-700 text-amber-300" },
    available: { text: "Available", cls: "bg-stone-900 border-stone-600 text-stone-300" },
    locked:    { text: "Locked", cls: "bg-stone-950 border-stone-800 text-stone-600" },
  }[status] || { text: status, cls: "bg-stone-900 border-stone-700 text-stone-400" };
  return (
    <span className={"text-[10px] font-numeric uppercase tracking-widest border rounded px-1.5 py-0.5 " + cfg.cls}>
      {cfg.text}
    </span>
  );
}

function StepIcon({ state }) {
  if (state === "done") {
    return (
      <div className="w-6 h-6 rounded-full bg-emerald-700/70 border border-emerald-500 flex items-center justify-center shrink-0">
        <Check size={12} className="text-emerald-100" strokeWidth={3} />
      </div>
    );
  }
  if (state === "current") {
    return (
      <div className="w-6 h-6 rounded-full bg-amber-700/60 border border-amber-500 flex items-center justify-center shrink-0 pulse-tick">
        <Circle size={10} className="text-amber-200 fill-amber-400" />
      </div>
    );
  }
  return (
    <div className="w-6 h-6 rounded-full bg-stone-900 border border-stone-700 flex items-center justify-center shrink-0">
      <Circle size={8} className="text-stone-600" />
    </div>
  );
}

function ProgressCheck({ item, contentItems }) {
  const c = item.check;
  const cur = Math.min(item.current, item.target);
  const pct = item.target > 0 ? Math.min(100, Math.round((cur / item.target) * 100)) : 100;
  const label = describeCheck(c, contentItems);
  return (
    <div className="pl-9 mt-1">
      <div className="flex items-center justify-between text-[10px] font-numeric text-stone-400">
        <span className="flex items-center gap-1.5">
          {c.type === "gather" && ITEM_ICON[c.item] && <span className="text-sm leading-none">{ITEM_ICON[c.item]}</span>}
          <span className={item.done ? "text-emerald-300" : ""}>{label}</span>
        </span>
        <span className={item.done ? "text-emerald-300" : "text-amber-300"}>
          {cur}/{item.target}
        </span>
      </div>
      <div className="xp-bar-outer mt-1" style={{ height: 4 }}>
        <div
          className={"xp-bar-fill " + (item.done ? "opacity-70" : "")}
          style={{ width: `${pct}%`, background: item.done ? "linear-gradient(180deg, #10b981 0%, #065f46 100%)" : undefined }}
        />
      </div>
    </div>
  );
}

function describeCheck(check, contentItems) {
  const t = check.type;
  if (t === "gather")   return `Gather ${check.qty}× ${itemName(check.item, contentItems)}`;
  if (t === "kill")     return `Defeat ${check.qty}× ${check.monster.replace(/_/g, " ")}`;
  if (t === "craft")    return `Craft with "${check.action.replace(/_/g, " ")}" ${check.qty}×`;
  if (t === "level")    return `Reach ${SKILL_LABEL[check.skill] || check.skill} Lv. ${check.level}`;
  if (t === "have")     return `Have ${check.qty}× ${itemName(check.item, contentItems)}`;
  if (t === "gold")     return `Have ${check.qty.toLocaleString()} gold`;
  if (t === "build")    return `Build a ${check.room.replace(/_/g, " ")} room`;
  if (t === "unlock_pet") return "Unlock any pet";
  if (t === "have_slayer_task") return "Accept a Slayer task";
  if (t === "reach_total_level") return `Reach Total Lv. ${check.value}`;
  if (t === "ge_fills") return `Complete ${check.qty} Grand Exchange trade${check.qty > 1 ? "s" : ""}`;
  return t;
}

function RewardChip({ icon: Icon, label, testid }) {
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-900/70 bg-amber-950/40 text-amber-200 font-numeric text-[10px]"
    >
      <Icon size={11} /> {label}
    </span>
  );
}

function RewardsRow({ rewards, contentItems }) {
  if (!rewards) return null;
  const chips = [];
  if (rewards.gold) chips.push(<RewardChip key="g" icon={Coins} label={`${rewards.gold} gp`} testid="reward-gold" />);
  for (const [skill, amt] of Object.entries(rewards.xp || {})) {
    chips.push(
      <RewardChip
        key={"x" + skill}
        icon={Sparkles}
        label={`+${amt} ${SKILL_LABEL[skill] || skill} XP`}
        testid={`reward-xp-${skill}`}
      />
    );
  }
  for (const [iid, iq] of rewards.items || []) {
    chips.push(
      <RewardChip
        key={"i" + iid}
        icon={Package}
        label={`${iq}× ${itemName(iid, contentItems)}`}
        testid={`reward-item-${iid}`}
      />
    );
  }
  if (chips.length === 0) return null;
  return <div className="flex flex-wrap gap-1.5">{chips}</div>;
}

function QuestCard({ quest, contentItems, onAccept, onAbandon, expanded, onToggle }) {
  const status = quest.status;
  const currentStep = quest.steps.find((s) => s.state === "current");
  const completedCount = quest.steps.filter((s) => s.state === "done").length;
  const total = quest.steps.length;
  const pct = Math.round((completedCount / total) * 100);

  return (
    <article
      className={"panel " + classForStatus(status)}
      data-testid={`quest-card-${quest.id}`}
    >
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-start justify-between gap-3 p-3 text-left hover:bg-stone-900/40 transition-colors"
        data-testid={`quest-toggle-${quest.id}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <ScrollText size={13} className={status === "completed" ? "text-emerald-400" : status === "active" ? "text-amber-400" : "text-stone-500"} />
            <span className="font-display text-base leading-tight text-stone-100 truncate">{quest.title}</span>
            <StatusBadge status={status} />
          </div>
          <div className="flex flex-wrap gap-3 text-[10px] font-numeric text-stone-500">
            <span className="flex items-center gap-1"><User size={10} /> {quest.giver}</span>
            <span className="flex items-center gap-1"><MapPin size={10} /> {quest.region}</span>
            <span className="text-amber-500">{completedCount}/{total} steps</span>
          </div>
          {currentStep && (
            <div className="mt-2 text-[11px] font-body text-stone-400 italic truncate">
              → {currentStep.objective}
            </div>
          )}
          <div className="mt-2 xp-bar-outer" style={{ height: 5 }}>
            <div
              className="xp-bar-fill"
              style={{
                width: `${pct}%`,
                background: status === "completed" ? "linear-gradient(180deg, #10b981 0%, #065f46 100%)" : undefined,
              }}
            />
          </div>
        </div>
        <ChevronDown
          size={14}
          className={"text-stone-500 mt-1 shrink-0 transition-transform " + (expanded ? "" : "-rotate-90")}
        />
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className="border-t border-stone-800/70 p-3 space-y-3">
          <p className="text-[12px] font-body italic text-stone-400">
            &ldquo;{quest.summary}&rdquo;
          </p>

          {/* Steps timeline */}
          <ol className="space-y-2" data-testid={`quest-steps-${quest.id}`}>
            {quest.steps.map((s, i) => (
              <li key={s.id} data-testid={`quest-step-${quest.id}-${i}`}>
                <div className="flex items-start gap-3">
                  <StepIcon state={s.state} />
                  <div className="flex-1 min-w-0">
                    <div
                      className={
                        "font-body text-sm leading-tight " +
                        (s.state === "done" ? "text-emerald-300 line-through" :
                          s.state === "current" ? "text-amber-200" : "text-stone-500")
                      }
                    >
                      {s.objective}
                    </div>
                    <div className="text-[11px] font-body italic text-stone-500 mt-0.5">
                      {s.narration}
                    </div>
                  </div>
                </div>
                {s.state === "current" && s.progress && s.progress.map((p, pi) => (
                  <ProgressCheck key={pi} item={p} contentItems={contentItems} />
                ))}
              </li>
            ))}
          </ol>

          {/* Rewards */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-500 font-numeric mb-1 flex items-center gap-1">
              <Trophy size={11} /> Rewards on completion
            </div>
            <RewardsRow rewards={quest.rewards} contentItems={contentItems} />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-1">
            {status === "available" && (
              <button
                onClick={() => onAccept(quest.id)}
                className="btn-gold text-xs px-3 py-1.5"
                data-testid={`accept-quest-${quest.id}`}
              >
                Accept quest
              </button>
            )}
            {status === "active" && quest.id !== "first_light" && (
              <button
                onClick={() => onAbandon(quest.id)}
                className="btn-ghost-gold text-xs px-3 py-1.5"
                data-testid={`abandon-quest-${quest.id}`}
              >
                Abandon
              </button>
            )}
            {status === "locked" && quest.prereq.length > 0 && (
              <div className="text-[10px] font-numeric text-stone-500 italic">
                Locked · requires: {quest.prereq.map((p) => p.replace(/_/g, " ")).join(", ")}
              </div>
            )}
          </div>
        </div>
      )}
    </article>
  );
}

export function QuestsPanel() {
  const { snap, content } = useGame();
  const [payload, setPayload] = useState(null);
  const [expandedIds, setExpandedIds] = useState({});
  const [filter, setFilter] = useState("all");

  const contentItems = content?.items || {};

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/api/game/quests");
      setPayload(data);
      // Auto-expand the top active quest first-time.
      setExpandedIds((prev) => {
        if (Object.keys(prev).length > 0) return prev;
        const nxt = {};
        const firstActive = data.quests.find((q) => q.status === "active");
        if (firstActive) nxt[firstActive.id] = true;
        return nxt;
      });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh, snap?.action?.id, snap?.combat?.monster_id, snap?.gold]);

  const onAccept = async (id) => {
    try {
      const { data } = await api.post("/api/game/quests/accept", { quest_id: id });
      setPayload(data);
      setExpandedIds((prev) => ({ ...prev, [id]: true }));
      toast.success("Quest accepted.");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const onAbandon = async (id) => {
    try {
      const { data } = await api.post("/api/game/quests/abandon", { quest_id: id });
      setPayload(data);
      toast.message("Quest abandoned.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const chapters = useMemo(() => {
    if (!payload) return [];
    const map = new Map();
    for (const q of payload.quests) {
      if (!map.has(q.chapter)) map.set(q.chapter, []);
      map.get(q.chapter).push(q);
    }
    return Array.from(map.entries());
  }, [payload]);

  const filtered = useMemo(() => {
    if (!payload) return chapters;
    if (filter === "all") return chapters;
    return chapters
      .map(([c, list]) => [c, list.filter((q) => q.status === filter)])
      .filter(([, list]) => list.length > 0);
  }, [chapters, filter, payload]);

  if (!payload) {
    return <div className="p-6 text-stone-500 italic font-body">Unrolling the quest scroll…</div>;
  }

  return (
    <div className="p-4 lg:p-6 space-y-3" data-testid="quests-panel">
      {/* Tabs */}
      <div className="flex items-center gap-2 text-xs font-body px-1 flex-wrap">
        {["all", "active", "available", "completed"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={
              "pb-1 px-2 capitalize " +
              (filter === f
                ? "text-amber-400 border-b-2 border-amber-500"
                : "text-stone-500 hover:text-amber-500")
            }
            data-testid={`quests-filter-${f}`}
          >
            {f}
          </button>
        ))}
        <div className="flex-1" />
        <div className="text-[10px] font-numeric text-stone-500 uppercase tracking-widest">
          Completed <span className="text-amber-400">{payload.counts.completed}</span> / {payload.counts.total}
        </div>
      </div>

      {/* Portrait band */}
      <div
        className="panel overflow-hidden relative"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(12,10,9,0.55), rgba(12,10,9,0.92)), url(/generated/hero_landing.png)`,
          backgroundSize: "cover", backgroundPosition: "center",
        }}
      >
        <div className="p-6 flex items-center gap-6">
          <div className="w-20 h-20 rounded-lg border-2 border-amber-700 bg-stone-950 flex items-center justify-center">
            <ScrollText size={36} className="text-amber-400" />
          </div>
          <div className="flex-1">
            <h1 className="font-display text-4xl heading-gilded">Quest Journal</h1>
            <p className="text-stone-400 font-body italic text-sm max-w-xl">
              A chronicle of who sent you where and why. Steps advance automatically when their conditions are met.
            </p>
            <div className="flex flex-wrap gap-4 mt-2 text-sm font-numeric text-stone-300">
              <span>Active · <span className="text-amber-300">{payload.counts.active}</span></span>
              <span>Available · <span className="text-amber-300">{payload.counts.available}</span></span>
              <span>Completed · <span className="text-emerald-300">{payload.counts.completed}</span></span>
            </div>
          </div>
        </div>
      </div>

      {/* Chapter list */}
      <div className="space-y-4">
        {filtered.length === 0 && (
          <div className="panel p-8 text-center text-stone-500 italic font-body">
            No quests in this view. Try another filter, or accept the next quest in a chapter to unlock more.
          </div>
        )}
        {filtered.map(([chapter, list]) => (
          <section key={chapter} data-testid={`chapter-${chapter.split(" ")[1] || chapter}`}>
            <div className="flex items-center gap-3 mb-2">
              <h2 className="font-display text-lg heading-gilded uppercase tracking-widest">{chapter}</h2>
              <div className="flex-1 border-t border-stone-800/70" />
              <span className="text-[10px] font-numeric text-stone-500">
                {list.filter((q) => q.status === "completed").length} / {list.length} done
              </span>
            </div>
            <div className="space-y-2">
              {list.map((q) => (
                <QuestCard
                  key={q.id}
                  quest={q}
                  contentItems={contentItems}
                  expanded={!!expandedIds[q.id]}
                  onToggle={() => setExpandedIds((prev) => ({ ...prev, [q.id]: !prev[q.id] }))}
                  onAccept={onAccept}
                  onAbandon={onAbandon}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
